/* ============================================================
   FLOOF MOTION
     - line-gated blur-in reveal (each line waits for its own scroll threshold)
     - marquees only animate while on screen, and SLOW rather than stop on hover
     - parallax: elements scroll at a different rate to the page .. DESKTOP ONLY
     - fully bypassed under prefers-reduced-motion
     - start-states are added by JS, so copy is never hidden behind a script
       that failed to run

   ------------------------------------------------------------
   NOTHING ON THIS PAGE MAY DEPEND ON AN OBSERVER FIRING TO BECOME VISIBLE.

   Read that before touching initReveals. It is the rule this file broke, and
   it cost a week of live traffic.

   The CSS has always had one failsafe:

     .floof:not(.f-anim-ready) .f-line, .f-rise, .f-pop { opacity: 1 }

   which covers the script never running at all. It does NOT cover the far
   nastier middle state: the script RUNS, adds .f-anim-ready (so every reveal
   target drops to opacity 0), and then the IntersectionObserver never reports
   an intersection. There was no timeout and no second chance, so the content
   stayed at opacity 0 FOREVER. A page that had loaded perfectly rendered as an
   empty white column.

   That is invisible in a desktop browser and reproducible in an embedded one.
   An in-app webview (Instagram, Facebook, TikTok) commonly runs its first
   layout before its host chrome has settled, so `window.innerHeight` can be 0
   at DOMContentLoaded. With vh = 0 the "already on screen" fast path below can
   never be true, so EVERY target went to the observer .. and an observer whose
   root has no height has nothing to intersect.

   The fix is three independent safety nets, in order of how gentle they are:

     1. a SWEEP that does the observer's job by hand, bound to scroll, resize,
        orientationchange and pageshow. If the observer works, the sweep finds
        everything already revealed and costs one rect read.
     2. an UNMEASURABLE VIEWPORT is treated as "show everything". A viewport we
        cannot measure is never a reason to hide the page.
     3. a 10s absolute backstop that reveals whatever is left.

   All three unbind themselves once nothing is pending.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* PHONE GATE. Used ONLY by initParallax .. see the long note there.
     Deliberately the same 749px breakpoint assets/floof-hero.css uses, because
     that file is what makes the parallax visually inert on a phone. If one
     moves, move the other or the two disagree and the effect comes back
     half-applied. */
  var narrow = window.matchMedia('(max-width: 749px)');

  /* ---------------------------------------------------------
     REVEAL

     ANYTHING ALREADY ON SCREEN AT BOOT REVEALS IMMEDIATELY, and that is not an
     optimisation .. it is a correctness fix.

     The observer's rootMargin is '0px 0px -12% 0px', which deliberately shrinks
     the trigger area up from the bottom so a thing reveals once it is properly in
     view rather than the instant one pixel of it appears. The side effect is a
     DEAD BAND: the lowest 12% of the first screen never intersects until you
     scroll. Anything sitting there at load stays invisible on a page the visitor
     is already looking at.

     That is exactly what happened to the hero's lime hashtag once it moved to
     y:100 as a half-cropped scroll cue .. it landed in the band and only turned up
     after a scroll. Nothing above the fold should ever wait for a scroll event, so
     the boot pass now checks the FULL viewport and reveals what is in it, honouring
     data-f-delay so the stagger still reads.
     --------------------------------------------------------- */

  /* every target the observer has not yet accounted for */
  var pending = [];
  var revealBound = false;
  var revealTick = false;

  /* Idempotent on purpose. The observer and the sweep can both reach the same
     element in the same frame, and double-firing would restart the transition
     and make it flicker. */
  function showNow(el) {
    if (el.__floofShown) return;
    el.__floofShown = true;
    var delay = parseInt(el.getAttribute('data-f-delay') || '0', 10);
    setTimeout(function () { el.classList.add('is-in'); }, delay);
  }

  /* documentElement.clientHeight is the fallback because an in-app webview can
     report innerHeight 0 while the document already has a usable height. */
  function viewportH() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function revealAll() {
    for (var i = 0; i < pending.length; i++) showNow(pending[i]);
    pending = [];
    unbindReveal();
  }

  /* The observer's job, done by hand. Cheap: one getBoundingClientRect per
     still-hidden element, throttled to a frame, and it stops binding anything
     the moment the list empties. */
  function sweep() {
    revealTick = false;
    if (!pending.length) return unbindReveal();

    var vh = viewportH();
    /* SAFETY NET 2. If we cannot measure the viewport we cannot decide what is
       on screen .. and the only safe answer to that is to show the page. */
    if (vh <= 0) return revealAll();

    var still = [];
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.__floofShown || !el.isConnected) continue;
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0 && r.width > 0) showNow(el);
      else still.push(el);
    }
    pending = still;
    if (!pending.length) unbindReveal();
  }

  function onRevealTick() {
    if (revealTick) return;
    revealTick = true;
    requestAnimationFrame(sweep);
  }

  /* pageshow covers coming BACK from checkout through the bfcache, where no
     scroll or resize fires but the observer may not re-deliver either. */
  function bindReveal() {
    if (revealBound) return;
    revealBound = true;
    window.addEventListener('scroll', onRevealTick, { passive: true });
    window.addEventListener('resize', onRevealTick, { passive: true });
    window.addEventListener('orientationchange', onRevealTick, { passive: true });
    window.addEventListener('pageshow', onRevealTick, { passive: true });
  }

  function unbindReveal() {
    if (!revealBound) return;
    revealBound = false;
    window.removeEventListener('scroll', onRevealTick);
    window.removeEventListener('resize', onRevealTick);
    window.removeEventListener('orientationchange', onRevealTick);
    window.removeEventListener('pageshow', onRevealTick);
  }

  function initReveals(root) {
    var targets = Array.prototype.slice.call(root.querySelectorAll('[data-f-reveal]'));
    if (!targets.length) return;

    if (reduce.matches || !('IntersectionObserver' in window)) {
      targets.forEach(showNow);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          showNow(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    var vh = viewportH();

    targets.forEach(function (el) {
      /* on screen right now, by the real viewport rather than the shrunk trigger
         area .. includes anything only partly visible, which is the whole point.
         Skipped entirely when vh is 0, because every comparison against 0 is a
         lie and the sweep below is what recovers from it. */
      if (vh > 0) {
        var r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0 && r.width > 0) {
          showNow(el);
          return;
        }
      }
      pending.push(el);
      io.observe(el);
    });

    if (!pending.length) return;

    /* SAFETY NET 1 .. and one deferred sweep, which is what actually rescues the
       zero-height-viewport boot: by 1200ms the webview has settled and a real
       height exists, so this reveals whatever is genuinely on screen. */
    bindReveal();
    setTimeout(sweep, 1200);

    /* SAFETY NET 3. Both of the above assume SOMETHING fires. If nothing does,
       show the page anyway. Losing the scroll stagger is a cosmetic bug; an
       invisible storefront is not. */
    setTimeout(revealAll, 10000);
  }

  /* ---------------------------------------------------------
     MARQUEE

     Duration comes from --dur-marquee, NOT --dur. --dur is a global token
     (240ms, the standard transition), so setting it on a rail both fails to
     reach the animation and silently redefines that token for everything
     inside the rail.
     --------------------------------------------------------- */

  /* A marquee is a visual loop, so its content is repeated to fill the rail and
     then cloned again for the seamless wrap. Invisible when the tiles are
     wordless decoration, but a rail carrying PRODUCT LINKS would otherwise
     announce the same product four times and drop every copy into the tab
     order .. on a moving element. Only the first set stays real. */
  function deactivate(el) {
    el.setAttribute('aria-hidden', 'true');
    var focusable = el.querySelectorAll('a[href], area[href], button, input, select, textarea, iframe, [tabindex]');
    for (var i = 0; i < focusable.length; i++) {
      focusable[i].setAttribute('tabindex', '-1');
    }
    if (el.matches && el.matches('a[href], button, [tabindex]')) {
      el.setAttribute('tabindex', '-1');
    }
  }

  /* Change the SPEED of a rail without a visual jump.

     Three things matter here and each one is a bug if you get it wrong:

     1. NEVER change animation-duration to change speed. A CSS animation's
        position is (elapsed time / duration), so rewriting duration re-maps
        where it currently is and the strip visibly leaps. updatePlaybackRate()
        exists precisely for this: it changes rate while holding the current
        position, which is the whole point of the "seamless" variant. The plain
        .playbackRate setter can jump, so it is only the fallback.

     2. APPLY IT TO EVERY TRACK IN THE RAIL, not just the first. There are two ..
        the original and the clone .. and the seamless loop depends on them
        moving in lockstep. Slowing one and not the other tears the seam open.

     3. If Web Animations is unavailable, fall back to the CSS class, which stops
        it dead. Worse than slowing, but never broken. */
  function setRate(rail, r) {
    var tracks = rail.querySelectorAll('.f-marquee__track');
    var found = false;

    for (var i = 0; i < tracks.length; i++) {
      if (!tracks[i].getAnimations) continue;
      var list = tracks[i].getAnimations();
      for (var j = 0; j < list.length; j++) {
        found = true;
        if (typeof list[j].updatePlaybackRate === 'function') {
          list[j].updatePlaybackRate(r);
        } else {
          list[j].playbackRate = r;
        }
      }
    }

    if (!found) rail.classList.toggle('is-held', r < 1);
  }

  function initMarquees(root) {
    var rails = root.querySelectorAll('[data-f-marquee]');
    if (!rails.length) return;

    rails.forEach(function (rail) {
      var track = rail.querySelector('.f-marquee__track');
      if (!track || track.dataset.cloned === 'true') return;

      var original = track.innerHTML;
      /* how many children are the ONE set assistive tech should see */
      var keep = track.children.length;

      /* rail.offsetWidth can be 0 on the same zero-height boot the reveal note
         above describes. Falling back to the viewport keeps the rail from being
         left with a single un-loopable set of cards. */
      var railW = rail.offsetWidth || document.documentElement.clientWidth || 0;

      var guard = 0;
      while (railW > 0 && track.scrollWidth < railW * 2 && guard < 8) {
        track.insertAdjacentHTML('beforeend', original);
        guard++;
      }

      /* everything past the first set is a visual repeat */
      var repeats = Array.prototype.slice.call(track.children, keep);
      repeats.forEach(deactivate);

      var clone = track.cloneNode(true);
      deactivate(clone);
      Array.prototype.slice.call(clone.children).forEach(deactivate);
      rail.appendChild(clone);
      track.dataset.cloned = 'true';

      /* A rail carrying links needs .f-marquee--pause in its class list.
         HOVER SLOWS IT, it does not stop it .. a dead stop under the cursor
         reads as the page breaking. A HELD FINGER stops it completely, which is
         different on purpose: on touch you are trying to hit a card, so the
         target has to hold still. */
      if (rail.classList.contains('f-marquee--pause')) {
        var slow = parseFloat(rail.getAttribute('data-slow'));
        if (!(slow > 0) || slow >= 1) slow = 0.18;

        rail.addEventListener('pointerenter', function () { setRate(rail, slow); });
        rail.addEventListener('focusin', function () { setRate(rail, slow); });

        var release = function () {
          rail.classList.remove('is-held');
          setRate(rail, 1);
        };

        rail.addEventListener('pointerleave', release);
        rail.addEventListener('focusout', function (e) {
          if (!rail.contains(e.relatedTarget)) release();
        });

        rail.addEventListener('pointerdown', function () {
          rail.classList.add('is-held');
        }, { passive: true });

        rail.addEventListener('pointerup', function () {
          /* stay still for a beat after release, so a tap that turns into a
             scroll does not restart the drift under the finger */
          setTimeout(function () { rail.classList.remove('is-held'); }, 900);
        }, { passive: true });

        rail.addEventListener('pointercancel', release, { passive: true });
      }

      if (reduce.matches) return;

      if (!('IntersectionObserver' in window)) {
        rail.classList.add('is-running');
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          rail.classList.toggle('is-running', e.isIntersecting);
        });
      }, { threshold: 0 });

      io.observe(rail);
    });
  }

  /* ---------------------------------------------------------
     PARALLAX
     Writes a --py offset which the element consumes as its own translate. It
     must own that property alone: the float keyframe animates translate too,
     and an animation always beats an inline style, so parallax and float live
     on SEPARATE nested elements.

     READS ARE BATCHED AHEAD OF WRITES. The first version measured one element
     and immediately wrote its style, then measured the next .. and a style write
     invalidates layout, so every getBoundingClientRect after the first forced a
     fresh synchronous reflow. Four objects meant four reflows PER SCROLL FRAME.
     An in-app browser also fires resize continuously while its toolbar slides
     away, so this ran far more often there than on desktop. Two passes, one
     read then one write, costs a single layout for the whole frame.

     ---------------------------------------------------------------
     !! IT DOES NOT RUN AT ALL BELOW 750px, AND THAT IS NOT A COMPROMISE.

     assets/floof-hero.css already contains, inside its max-width:749px block:

         .f-stagehero [data-parallax] { translate: 0 0; }

     so on a phone the CSS throws away every --py this code writes. The effect
     has been visually DEAD on mobile since the objects were pulled from the
     phone hero .. but the JavaScript never knew, so it kept binding a scroll
     and a resize listener and doing a read pass plus a write pass on every
     single frame, to produce a value nothing consumes.

     That is the worst shape a performance bug can take: full cost, zero output,
     and invisible in every metric that looks at bytes. It also lands hardest
     exactly where this store's traffic is .. reported 2026-08-22 as freezing
     and lagging in the Instagram in-app browser on a low-end Android, which is
     the one environment that fires resize continuously (see the paragraph
     above) AND has the least CPU to absorb it.

     TBT WILL NOT SHOW THIS. Total Blocking Time only counts long tasks during
     load; a scroll handler that jitters every frame afterwards is scroll jank,
     which Lighthouse does not measure at all. So do not expect the PageSpeed
     number to move much .. this is a fix for what the visitor FEELS, and the
     two are not the same thing.

     IF THE OBJECTS EVER COME BACK ON THE PHONE HERO: delete the `translate: 0 0`
     line in floof-hero.css and this gate together, in the same change. Removing
     one without the other gives you either a dead effect that still costs a
     frame, or a live effect the layout was never checked against.
     --------------------------------------------------------------- */
  var parallaxItems = [];
  var parallaxBound = false;
  var ticking = false;

  function measure() {
    ticking = false;

    var vh = window.innerHeight || 1;
    var reads = [];

    /* READ PASS .. no style writes in this loop, at all */
    for (var i = 0; i < parallaxItems.length; i++) {
      var el = parallaxItems[i];
      var host = el.closest('[data-parallax-scope]') || el.parentElement;
      if (!host) continue;

      var rect = host.getBoundingClientRect();
      if (rect.bottom < -240 || rect.top > vh + 240) continue;

      reads.push({ el: el, top: rect.top, h: rect.height });
    }

    /* WRITE PASS */
    for (var j = 0; j < reads.length; j++) {
      var r = reads[j];
      var progress = (vh - r.top) / (vh + r.h);
      var speed = parseFloat(r.el.getAttribute('data-parallax')) || 0;
      var shift = (0.5 - progress) * speed * 240;
      r.el.style.setProperty('--py', shift.toFixed(1) + 'px');
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  }

  function initParallax(root) {
    if (reduce.matches) return;

    /* the phone gate .. see the note above. checked here rather than at the
       listener so nothing is bound and nothing is measured, not even once. */
    if (narrow.matches) return;

    var found = root.querySelectorAll('[data-parallax]');
    if (!found.length) return;

    found.forEach(function (el) {
      if (parallaxItems.indexOf(el) === -1) parallaxItems.push(el);
    });

    if (!parallaxBound) {
      parallaxBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }

    measure();
  }

  /* ---------------------------------------------------------
     BOOT
     --------------------------------------------------------- */
  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll('.floof').forEach(function (s) {
      s.classList.add('f-anim-ready');
    });
    initReveals(root);
    initMarquees(root);
    initParallax(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* the theme editor re-renders sections in place */
  document.addEventListener('shopify:section:load', function (e) {
    parallaxItems = parallaxItems.filter(function (el) { return el.isConnected; });
    boot(e.target);
  });
})();